import { objectEntries, type ReadonlyRecord, splitByLength } from '../../../utils'

import { Capability, type Certificate, CertificateExtendedKeyUsage, CertificateKeyUsage, ConnectwareError, ConnectwareErrorType } from '../../../domain'

import type { CertificatesService } from '../../../application'

import { extractValidatedJson, FetchConnectwareHTTPService } from '../Base'
import type { CertificateResponse } from './Types'
import { isMinimalCertificatesResponse, isMinimalNonEmptyCertificatesResponse } from './Validators'

/**
 * @see https://github.com/fidm/x509/blob/4acca1b2919832178fd52c88f2f1ecb12a14088f/src/x509.ts#L449
 */
const keyUsageMap: ReadonlyRecord<string, CertificateKeyUsage> = {
    critical: CertificateKeyUsage.CRITICAL,
    cRLSign: CertificateKeyUsage.CRL_SIGN,
    dataEncipherment: CertificateKeyUsage.DATA_ENCIPHERMENT,
    decipherOnly: CertificateKeyUsage.DECIPHER_ONLY,
    digitalSignature: CertificateKeyUsage.DIGITAL_SIGNATURE,
    encipherOnly: CertificateKeyUsage.ENCIPHER_ONLY,
    keyAgreement: CertificateKeyUsage.KEY_AGREEMENT,
    keyCertSign: CertificateKeyUsage.KEY_CERTSIGN,
    keyEncipherment: CertificateKeyUsage.KEY_ENCIPHERMENT,
    nonRepudiation: CertificateKeyUsage.NON_REPUDIATION,
}

/**
 * @see https://github.com/fidm/x509/blob/4acca1b2919832178fd52c88f2f1ecb12a14088f/src/x509.ts#L449
 */
const extendedKeyUsageMap: ReadonlyRecord<string, CertificateExtendedKeyUsage> = {
    critical: CertificateExtendedKeyUsage.CRITICAL,
    clientAuth: CertificateExtendedKeyUsage.CLIENT_AUTH,
    codeSigning: CertificateExtendedKeyUsage.CODE_SIGNING,
    emailProtection: CertificateExtendedKeyUsage.EMAIL_PROTECTION,
    OCSPSigning: CertificateExtendedKeyUsage.OCSP_SIGNING,
    serverAuth: CertificateExtendedKeyUsage.SERVER_AUTH,
    timeStamping: CertificateExtendedKeyUsage.TIME_STAMPING,
}

const mapUsageResponse = <T extends string>(usagesResponse: readonly string[], map: ReadonlyRecord<string, T>): readonly T[] =>
    objectEntries(map).reduce<T[]>((usages, [name, usage]) => (usagesResponse.includes(name) ? [...usages, usage] : usages), [])

const mapReponse = ({
    id,
    issuedBy,
    issuedTo,
    keyUsage,
    extendedKeyUsage,
    basicConstraints,
    fingerprints,
    issuedOn,
    expiresOn,
    removable,
}: CertificateResponse): Certificate => ({
    id,
    issuedBy: { ...issuedBy, commonName: issuedBy.commonName || null },
    issuedTo: { ...issuedTo, commonName: issuedTo.commonName || null },

    keyUsage: mapUsageResponse(keyUsage, keyUsageMap),
    extendedKeyUsage: mapUsageResponse(extendedKeyUsage, extendedKeyUsageMap),

    basicConstraints: basicConstraints.isCA ? { authority: true, pathLength: basicConstraints.pathLength } : { authority: false },

    issuedOn: new Date(issuedOn),
    expiresOn: new Date(expiresOn),

    fingerprints: { sha1: splitByLength(fingerprints.sha1, 2).join(':'), sha256: splitByLength(fingerprints.sha256, 2).join(':') },
    removable,
})

export class ConnectwareHTTPCertificatesService extends FetchConnectwareHTTPService implements CertificatesService {
    fetchCertificates (): Promise<Certificate[]> {
        return this.request<'/api/certificates', 'GET', Certificate[]>({
            capability: Capability.CERTIFICATES_MANAGE,
            method: 'GET',
            path: '/api/certificates',
            authenticate: true,
            handlers: {
                200: (response) => extractValidatedJson(response, isMinimalCertificatesResponse).then((data) => data.map(mapReponse)),
                500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Could not load certificates'),
            },
        })
    }

    delete ({ id }: Certificate): Promise<void> {
        return this.request<'/api/certificates', 'DELETE', void>({
            capability: Capability.CERTIFICATES_MANAGE,
            method: 'DELETE',
            path: '/api/certificates',
            authenticate: true,
            queryParams: { id },
            handlers: {
                200: () => Promise.resolve(),
                400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected bad request detected while deleting certificates', { id }),
                404: () => new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'Could not find certificate for deletion', { id }),
                500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Could not delete certificates', { id }),
            },
        })
    }

    upload (certificateFile: string): Promise<Certificate> {
        return this.request<'/api/certificates', 'POST', Certificate>({
            capability: Capability.CERTIFICATES_MANAGE,
            method: 'POST',
            path: '/api/certificates',
            authenticate: true,
            body: certificateFile,
            handlers: {
                201: (response) => extractValidatedJson(response, isMinimalNonEmptyCertificatesResponse).then(([cert]) => mapReponse(cert)),
                400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected bad request detected while uploading certificate'),
            },
        })
    }

    fetchCertificateFileContent ({ id }: Certificate): Promise<string> {
        return this.request<'/api/certificates/content', 'GET', string>({
            capability: Capability.CERTIFICATES_MANAGE,
            method: 'GET',
            path: '/api/certificates/content',
            authenticate: true,
            queryParams: { id },
            handlers: {
                200: (response) => response.getJson(),
                400: () =>
                    new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected bad request detected while fetching certificate file content', { id }),
                404: () => new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'Could not find certificate', { id }),
                500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Could not delete certificates', { id }),
            },
        })
    }
}
